import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { GRADES, SubjectTabs, StatTile, SectionTitle, LessonCard, isPending, subjectMeta } from "@/components/Kit";
import MaterialViewer from "@/components/MaterialViewer";
import ExamModal from "@/components/ExamModal";
import SubmissionDetail from "@/components/SubmissionDetail";
import Calendar from "@/components/Calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  FileVideo, Loader2, ListChecks, ScanLine, Upload, Trophy, BookOpen, Sparkles,
  CheckCircle2, GraduationCap, ChevronDown, CalendarDays,
} from "lucide-react";


function GradeGate({ open, onDone }) {
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const enter = async () => {
    if (!choice) return;
    setBusy(true);
    try { await api.post("/student/select-grade", { grade: choice }); await onDone(); }
    catch (e) { toast.error("Could not set grade"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open}>
      <DialogContent hideClose onEscapeKeyDown={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()} className="bg-[#0E1420] border-[#1E293B] text-slate-100 max-w-md" data-testid="grade-gate-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3"><GraduationCap className="w-6 h-6 text-emerald-400" /></div>
          <DialogTitle className="font-display text-2xl">Choose your grade</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-400 -mt-1 mb-2">Select your grade to unlock your lessons. (Your teacher can change this later.)</p>
        <div className="grid grid-cols-2 gap-2">
          {GRADES.map((g) => (
            <button key={g} data-testid={`grade-choice-${g}`} onClick={() => setChoice(g)}
              className={`px-4 py-4 rounded-xl border text-sm font-semibold transition-all ${choice === g ? "bg-emerald-500/15 border-emerald-500 text-emerald-300" : "bg-[#090D16] border-[#1E293B] text-slate-300 hover:border-[#334155]"}`}>
              {g}
            </button>
          ))}
        </div>
        <button data-testid="enter-grade-button" onClick={enter} disabled={!choice || busy} className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-emerald-950 font-semibold rounded-xl py-3 hover:bg-emerald-400 transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Start Learning
        </button>
      </DialogContent>
    </Dialog>
  );
}

function StudentMaterialDialog({ material, open, onClose, onActivity }) {
  const [exams, setExams] = useState([]);
  const [activeExam, setActiveExam] = useState(null);
  const [grading, setGrading] = useState(false);
  const [gradeResult, setGradeResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && material) { setGradeResult(null); api.get(`/exams?material_id=${material.id}`).then((r) => setExams(r.data)).catch(() => {}); }
  }, [open, material]);
  if (!material) return null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGrading(true); setGradeResult(null);
    try {
      const fd = new FormData(); fd.append("material_id", material.id); fd.append("file", file);
      const r = await api.post("/grade", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGradeResult(r.data); toast.success("Homework graded!"); onActivity && onActivity();
    } catch (err) { toast.error(err?.response?.data?.detail || "Grading failed"); }
    finally { setGrading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-[#0E1420] border-[#1E293B] text-slate-100 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="student-material-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"><BookOpen className="w-5 h-5 text-emerald-400" /> {material.title}</DialogTitle>
            <div className="text-sm text-slate-500">{material.grade} · {material.subject}</div>
          </DialogHeader>

          <MaterialViewer material={material} />

          <div className="grid md:grid-cols-2 gap-4 mt-2">
            <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><ListChecks className="w-4 h-4 text-indigo-400" /><h3 className="font-semibold">Exams</h3></div>
              <div className="space-y-2">
                {exams.length === 0 && <div className="text-sm text-slate-600">No exam available yet.</div>}
                {exams.map((q) => (
                  <div key={q.id} className="flex items-center justify-between p-3 rounded-xl bg-[#090D16] border border-[#1E293B]">
                    <span className="text-sm text-slate-300">{q.num_mcq} MCQ · {q.num_subjective} written</span>
                    <button data-testid={`take-exam-${q.id}`} onClick={() => setActiveExam(q.id)} className="inline-flex items-center gap-1.5 bg-indigo-500 text-white text-sm font-medium rounded-lg px-3 py-1.5 hover:bg-indigo-400 transition-colors"><Sparkles className="w-3.5 h-3.5" /> Take</button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3"><ScanLine className="w-4 h-4 text-amber-400" /><h3 className="font-semibold">Submit Homework</h3></div>
              <input ref={fileRef} data-testid="upload-homework-input" type="file" accept=".pdf,image/*" onChange={upload} className="hidden" />
              <button data-testid="upload-homework-button" onClick={() => fileRef.current?.click()} disabled={grading}
                className="w-full border-2 border-dashed border-[#1E293B] rounded-xl py-6 flex flex-col items-center gap-2 text-slate-400 hover:border-amber-500/50 hover:text-amber-400 transition-colors disabled:opacity-50">
                {grading ? <><Loader2 className="w-6 h-6 animate-spin" /><span className="text-sm">Grading…</span></> : <><Upload className="w-6 h-6" /><span className="text-sm">Upload PDF or handwritten image</span></>}
              </button>
              {gradeResult && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 p-4 rounded-xl bg-[#090D16] border border-amber-500/30" data-testid="grade-result">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm text-slate-400">Score</span><span className="text-2xl font-extrabold font-display text-amber-400">{gradeResult.score}<span className="text-base text-slate-500">/{gradeResult.max_score}</span></span></div>
                  <div className="text-sm text-slate-300">{gradeResult.ai_feedback}</div>
                </motion.div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ExamModal examId={activeExam} open={!!activeExam} onClose={() => setActiveExam(null)} onSubmitted={() => onActivity && onActivity()} />
    </>
  );
}

export default function StudentDashboard() {
  const { user, checkAuth } = useAuth();
  const [subject, setSubject] = useState("Physics");
  const [materials, setMaterials] = useState([]);
  const [subs, setSubs] = useState([]);
  const [openMaterial, setOpenMaterial] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [view, setView] = useState("feed");

  const grade = user?.grade;
  const gateOpen = !grade;

  const loadFeed = useCallback(() => {
    if (!grade) return;
    api.get(`/materials?grade=${encodeURIComponent(grade)}&subject=${encodeURIComponent(subject)}`).then((r) => setMaterials(r.data)).catch(() => {});
    api.get(`/submissions?mine=true`).then((r) => setSubs(r.data)).catch(() => {});
  }, [grade, subject]);
  useEffect(() => { loadFeed(); }, [loadFeed]);
  useEffect(() => {
    if (!grade || !materials.some(isPending)) return;
    const t = setInterval(loadFeed, 5000);
    return () => clearInterval(t);
  }, [materials, grade, loadFeed]);

  const readyMaterials = materials.filter((m) => (m.status || "ready") === "ready");
  const avg = subs.length ? Math.round(subs.reduce((a, s) => a + (Number(s.percentage) || 0), 0) / subs.length) : null;
  const nav = [{ id: "feed", label: "Lessons", icon: BookOpen }, { id: "calendar", label: "Calendar", icon: CalendarDays }];

  return (
    <AppShell nav={nav} active={view} onNav={setView}>
      <GradeGate open={gateOpen} onDone={checkAuth} />
      {grade && (
        <>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-12 gap-4 mb-10">
            <div className="lg:col-span-8 relative overflow-hidden rounded-3xl border border-[#1E293B] bg-[#111726] p-7 sm:p-9">
              <img src={subjectMeta(subject).img} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#111726] via-[#111726]/85 to-transparent" />
              <div className="relative">
                <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400" data-testid="student-grade-label">{grade}</div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-display tracking-tight mt-2">Hi {user?.name?.split(" ")[0] || "there"}, ready to learn?</h1>
                <p className="text-slate-400 mt-3 max-w-lg text-sm sm:text-base">Watch the recording, revise with notes and mindmaps, then test yourself.</p>
              </div>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-4">
              <StatTile icon={BookOpen} label="Lessons" value={readyMaterials.length} color="#22D3EE" />
              <StatTile icon={Trophy} label="Average" value={avg === null ? "—" : `${avg}%`} color="#F59E0B" />
              <div className="col-span-2"><StatTile icon={CheckCircle2} label="Attempts" value={subs.length} color="#10B981" /></div>
            </div>
          </motion.div>

          {view === "calendar" ? <Calendar fixedGrade={grade} /> : (
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-8">
                <SectionTitle overline="Library" title={`${subject} lessons`} />
                <div className="mb-6"><SubjectTabs value={subject} onChange={setSubject} /></div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {readyMaterials.length === 0 && <div className="sm:col-span-2 rounded-2xl border border-dashed border-[#1E293B] p-12 text-center text-slate-500" data-testid="student-empty-lessons">No {subject} lessons yet. Check back soon.</div>}
                  {readyMaterials.map((m) => (
                    <LessonCard key={m.id} m={m} testid={`student-material-card-${m.id}`} onOpen={() => setOpenMaterial(m)} />
                  ))}
                </div>
              </div>

              <div className="lg:col-span-4">
                <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5 lg:sticky lg:top-8" data-testid="my-results-panel">
                  <div className="flex items-center gap-2 mb-4"><Trophy className="w-5 h-5 text-amber-400" /><h2 className="text-lg font-bold font-display">My results</h2></div>
                  <div className="space-y-2 max-h-[520px] overflow-y-auto">
                    {subs.length === 0 && <div className="text-sm text-slate-500">No results yet. Take an exam or upload homework.</div>}
                    {subs.map((s) => (
                      <div key={s.id} className="rounded-xl bg-[#090D16] border border-[#1E293B]" data-testid={`result-item-${s.id}`}>
                        <button onClick={() => setExpanded(expanded === s.id ? null : s.id)} className="w-full p-3 text-left">
                          <div className="flex items-center justify-between">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${s.type === "exam" ? "bg-indigo-500/15 text-indigo-300" : "bg-amber-500/15 text-amber-300"}`}>{s.type === "exam" ? "Exam" : "Homework"}</span>
                            <div className="flex items-center gap-2"><span className="text-sm font-bold text-emerald-400">{s.percentage}%</span><ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${expanded === s.id ? "rotate-180" : ""}`} /></div>
                          </div>
                          <div className="h-1 rounded-full bg-[#1E293B] mt-2.5 overflow-hidden"><div className="h-full bg-emerald-400 rounded-full" style={{ width: `${Math.min(100, Number(s.percentage) || 0)}%` }} /></div>
                          <div className="text-xs text-slate-400 mt-2 truncate">{s.exam_title} · {s.subject}</div>
                        </button>
                        {expanded === s.id && <div className="px-3 pb-3"><SubmissionDetail submission={s} /></div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      <StudentMaterialDialog material={openMaterial} open={!!openMaterial} onClose={() => setOpenMaterial(null)} onActivity={loadFeed} />
    </AppShell>
  );
}
